import React, {useEffect, useRef, useState} from "react"
import { graphql } from "gatsby"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger";
import useTeamQuery from "../../../hooks/use-team-query"
import TeamCard from "../../cards/team"
import { Logo, Search, Close, Expand, Collapse } from "../../icons"
import parse from "html-react-parser"
import StructuredText from "../custom-structured-text-renderer"

gsap.registerPlugin(ScrollTrigger);

const TeamArchive = ({title, richContent, slug}) => {
  const elementRef = useRef(null)
  const {allDatoCmsTeamMember, allDatoCmsLocation, allDatoCmsServicesTag} = useTeamQuery()

  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState(false)
  const [selectedSectors, setSelectedSectors] = useState([])
  const [selectedServices, setSelectedServices] = useState([])
  const [selectedLocations, setSelectedLocations] = useState([])
  const [filteredTeam, setFilteredTeam] = useState(allDatoCmsTeamMember?.nodes || [])
  const [expandedFilters, setExpandedFilters] = useState([]) // Track multiple expanded filters

  const toggleSelection = (selectedArray, setSelectedArray, item) => {
    if (selectedArray.includes(item)) {
      setSelectedArray(selectedArray.filter(selected => selected !== item))
    } else {
      setSelectedArray([...selectedArray, item])
    }
  }

  const toggleFilterExpand = (filterName) => {
    if (expandedFilters.includes(filterName)) {
      setExpandedFilters(expandedFilters.filter(name => name !== filterName)) // Collapse
    } else {
      setExpandedFilters([...expandedFilters, filterName]) // Expand
    }
  }

  useEffect(() => {
    let filtered = allDatoCmsTeamMember?.nodes || []

    if (searchTerm) {
      filtered = filtered.filter(team =>
        team.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedServices.length > 0) {
      filtered = filtered.filter(team =>
        team.services.some(tag => selectedServices.includes(tag.title))
      )
    }

    if (selectedLocations.length > 0) {
      filtered = filtered.filter(team =>
        team.locations.some(location => selectedLocations.includes(location.title))
      )
    }

    setFilteredTeam(filtered)
  }, [searchTerm, selectedServices, selectedLocations, allDatoCmsTeamMember?.nodes])

  const getHeadingText = () => {
    let heading = `${title}`

    const activeFilters = []
    if (selectedSectors.length > 0) activeFilters.push(selectedSectors.join(" / "))
    if (selectedServices.length > 0) activeFilters.push(selectedServices.join(" / "))
    if (selectedLocations.length > 0) activeFilters.push(selectedLocations.join(" / "))

    if (activeFilters.length > 0) {
      heading = `${filteredTeam.length} Results for<br />${activeFilters.join(" / ")}${searchTerm ? ` / ${searchTerm}"` : ""}`
    }

    return parse(heading)
  }

  const isFilterActive = () => {
    return searchTerm || selectedSectors.length > 0 || selectedServices.length > 0 || selectedLocations.length > 0
  }

  const clearFilters = () => {
    setSearchTerm("")
    setSelectedSectors([])
    setSelectedServices([])
    setSelectedLocations([])
  }

  useEffect(() => {
    const line = elementRef.current?.querySelectorAll('.line');

    ScrollTrigger.create({
      trigger: elementRef.current,
      start: "top bottom",
      onEnter: () => {
        line.forEach((el) => el.classList.add('is-inview'))
      },
    });

    return () => {
    };
  }, [slug]);

  return (
    <>
      <section className="page-banner page-banner--team">
        <div className="page-banner__inner">
          <div className='page-banner__content'>
            <div className='page-banner__left'>
              <h1>{getHeadingText()}</h1>
            </div>
            { richContent &&
              <div className='page-banner__right'>
                {richContent && <StructuredText data={richContent} />}
              </div>
            }
          </div>
        </div>
      </section>
      <section className="team-archive" ref={elementRef}>
        <div className="team-archive__inner">
          <div className="team-archive__wrapper">
            <div className="team-archive__filter">
              {isFilterActive() ? (
                <button className="btn" onClick={clearFilters}>Clear Search <Close /></button>
              ) : (
                <button className="btn btn--orange" onClick={() => setFilter(!filter)}>Filter</button>
              )}
              <div className="line line--horizon center-left"></div>
            </div>

            <div className="team-archive__items">
              <div className="line line--vertical center-top"></div>
              {filteredTeam?.length > 4 && <div className="line line--vertical center-bottom"></div>}
              {filteredTeam?.length > 4 && <div className="line line--vertical center-top"></div>}
              {
                filteredTeam?.map((el, i) => (
                  <div className="team-archive__item">
                  <TeamCard key={i} {...el} />
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </section>
      <section className={`filter${filter ? " filter--active" : ""}`}>
        <div className="filter__inner">
          <div className="filter__header">
            <Logo />
            <button className="btn btn--border-white" onClick={() => setFilter(!filter)}>Close</button>
          </div>
          <div className="filter__search">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <button><Search /></button>
          </div>
          <div className="filter__items">
            <div className={`filter__item ${expandedFilters.includes("service") ? "expanded" : ""}`}>
              <h4>
                <button onClick={() => toggleFilterExpand("service")}>
                  by Service <span>{expandedFilters.includes("service") ? <Collapse /> : <Expand />}</span>
                </button>
              </h4>
              <ul>
                {allDatoCmsServicesTag?.nodes.map((el, i) => (
                  <li key={i}>
                    <button
                      onClick={() => toggleSelection(selectedServices, setSelectedServices, el.title)}
                      className={selectedServices.includes(el.title) ? "active" : ""}
                    >
                      {el.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`filter__item ${expandedFilters.includes("location") ? "expanded" : ""}`}>
              <h4>
                <button onClick={() => toggleFilterExpand("location")}>
                  by Location <span>{expandedFilters.includes("location") ? <Collapse /> : <Expand />}</span>
                </button>
              </h4>
              <ul>
                {allDatoCmsLocation?.nodes.map((el, i) => (
                  <li key={i}>
                    <button
                      onClick={() => toggleSelection(selectedLocations, setSelectedLocations, el.title)}
                      className={selectedLocations.includes(el.title) ? "active" : ""}
                    >
                      {el.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <button className="filter__excess" onClick={() => setFilter(!filter)} />
      </section>
    </>
  )
}

export default TeamArchive

export const query = graphql`
  fragment TeamArchiveProps on DatoCmsTeamArchive {
    ... on DatoCmsTeamArchive {
      id
      title
      richContent {
        value
      }
    }
  }
`
